import React, { useMemo, useState } from "react";
import {
  Card,
  CardActionArea,
  Typography,
  Box,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CollectionsIcon from "@mui/icons-material/Collections";

const FALLBACK_IMG =
  "https://rmrstorage.blob.core.windows.net/measite/MeaLogoBlackTrans.png";

export default function MenuCard({
  item,
  hasOptions,
  selectedQty,
  onQtyChange,
  onAdd,
  outOfStock = false,
  disabledReason = "",
  disabledLabel = "",
  disabledButtonText = "",
  fixedWidth = 350,
  fixedHeight = 340,
}) {
  const price = Number(item?.basePrice || 0);
  const fullDesc = item?.description || "";
  const MAX_LEN = 110;

  const isLong = fullDesc.length > MAX_LEN;
  const shortDesc = isLong
    ? `${fullDesc.slice(0, MAX_LEN).trimEnd()}...`
    : fullDesc;

  const [descOpen, setDescOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  const images = useMemo(() => {
    const raw = Array.isArray(item?.images) ? item.images : [];

    const cleaned = raw
      .map((img, idx) => ({
        imageUrl: img?.imageUrl || "",
        thumbnailUrl: img?.thumbnailUrl || "",
        altText: img?.altText || item?.name || `Image ${idx + 1}`,
        displayOrder: img?.displayOrder ?? idx,
        isPrimary: !!img?.isPrimary,
        active: img?.active !== false,
      }))
      .filter((img) => img.active && img.imageUrl)
      .sort((a, b) => {
        if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
        return (a.displayOrder ?? 0) - (b.displayOrder ?? 0);
      });

    if (cleaned.length > 0) return cleaned;

    return [
      {
        imageUrl: item?.imageUrl || FALLBACK_IMG,
        thumbnailUrl: "",
        altText: item?.name || "Menu item",
        displayOrder: 0,
        isPrimary: true,
        active: true,
      },
    ];
  }, [item]);

  const heroImage = images[0]?.imageUrl || item?.imageUrl || FALLBACK_IMG;
  const modalImage = images[modalImageIndex]?.imageUrl || heroImage;

  const handleCardClick = () => {
    if (!outOfStock) {
      setModalImageIndex(0);
      setDescOpen(true);
    }
  };

  const handleViewFullClick = (e) => {
    e.stopPropagation();
    setModalImageIndex(0);
    setDescOpen(true);
  };

  const handleDialogClose = () => setDescOpen(false);

  const goPrev = () => {
    setModalImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goNext = () => {
    setModalImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const ribbonText = outOfStock ? disabledLabel || "Out of stock" : "";
  const btnText = outOfStock
    ? disabledButtonText || "Out"
    : hasOptions
    ? "Customize"
    : "Add";

  return (
    <>
      <Card
        elevation={2}
        sx={{
          width: fixedWidth,
          height: fixedHeight,
          borderRadius: 2,
          overflow: "hidden",
          position: "relative",
          boxShadow: "0 8px 24px rgba(0,0,0,.08)",
          "&:hover img": {
            transform: outOfStock ? "none" : "scale(1.04)",
          },
        }}
      >
        <CardActionArea
          component="div"
          onClick={handleCardClick}
          sx={{
            width: "100%",
            height: "100%",
            position: "relative",
            cursor: outOfStock ? "default" : "pointer",
          }}
        >
          <Box
            component="img"
            src={heroImage}
            alt={item?.name || "Menu item"}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = FALLBACK_IMG;
            }}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform .35s ease, filter .2s ease, opacity .2s ease",
              willChange: "transform",
              filter: outOfStock ? "grayscale(60%) brightness(0.8)" : "none",
              opacity: outOfStock ? 0.85 : 1,
            }}
          />

          {price > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                px: 1.1,
                py: 0.4,
                borderRadius: 999,
                bgcolor: "rgba(255,255,255,0.9)",
                backdropFilter: "blur(6px)",
                fontWeight: 800,
                fontSize: 13,
                border: "1px solid rgba(0,0,0,.08)",
                boxShadow: "0 6px 18px rgba(0,0,0,.12)",
                opacity: outOfStock ? 0.7 : 1,
              }}
            >
              ${price.toFixed(2)}
            </Box>
          )}

          {images.length > 1 && (
            <Box
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                px: 1,
                py: 0.35,
                borderRadius: 999,
                bgcolor: "rgba(0,0,0,0.65)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              <CollectionsIcon sx={{ fontSize: 15 }} />
              {images.length}
            </Box>
          )}

          {outOfStock && (
            <Box
              sx={{
                position: "absolute",
                top: 12,
                left: -40,
                px: 6,
                py: 0.5,
                transform: "rotate(-15deg)",
                bgcolor: "rgba(17,17,17,0.85)",
                color: "#fff",
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: 0.6,
                textTransform: "uppercase",
                border: "1px solid rgba(255,255,255,0.15)",
                boxShadow: "0 6px 18px rgba(0,0,0,.2)",
                pointerEvents: "none",
              }}
            >
              {ribbonText}
            </Box>
          )}

          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              px: 1.25,
              py: 1,
              display: "flex",
              flexDirection: "column",
              gap: 0.4,
              bgcolor: "rgba(0,0,0,0.55)",
              backdropFilter: "blur(6px)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="subtitle2"
                noWrap
                sx={{ color: "#fff", fontWeight: 800, flex: 1 }}
                title={item?.name}
              >
                {item?.name}
              </Typography>

              <Tooltip
                title={outOfStock ? disabledReason || "Currently unavailable" : ""}
              >
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={outOfStock}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!outOfStock) onAdd?.();
                    }}
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 800,
                      py: 0.4,
                      px: 1.4,
                    }}
                  >
                    {btnText}
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {outOfStock && disabledReason && (
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 700 }}
              >
                {disabledReason}
              </Typography>
            )}

            {fullDesc && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 0.75,
                  mt: 0.2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.9)",
                    fontSize: "0.78rem",
                    lineHeight: 1.35,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {shortDesc}
                </Typography>

                {isLong && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={handleViewFullClick}
                    sx={{
                      color: "rgba(255,255,255,0.9)",
                      textTransform: "none",
                      fontSize: "0.7rem",
                      p: 0,
                      minWidth: "auto",
                    }}
                  >
                    View full
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </CardActionArea>
      </Card>

      <Dialog open={descOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          <Typography variant="h6" sx={{ mr: 1 }}>
            {item?.name}
          </Typography>
          <IconButton onClick={handleDialogClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {!!images.length && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={modalImage || FALLBACK_IMG}
                  alt={images[modalImageIndex]?.altText || item?.name}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = FALLBACK_IMG;
                  }}
                  sx={{
                    width: "100%",
                    borderRadius: 1,
                    objectFit: "cover",
                    maxHeight: 320,
                    display: "block",
                  }}
                />

                {images.length > 1 && (
                  <>
                    <IconButton
                      onClick={goPrev}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: 8,
                        transform: "translateY(-50%)",
                        bgcolor: "rgba(0,0,0,0.45)",
                        color: "#fff",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                      }}
                    >
                      <ChevronLeftIcon />
                    </IconButton>

                    <IconButton
                      onClick={goNext}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        right: 8,
                        transform: "translateY(-50%)",
                        bgcolor: "rgba(0,0,0,0.45)",
                        color: "#fff",
                        "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                      }}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </>
                )}
              </Box>

              {images.length > 1 && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    mt: 1.25,
                    overflowX: "auto",
                    pb: 0.5,
                  }}
                >
                  {images.map((img, idx) => (
                    <Box
                      key={`${img.imageUrl}-${idx}`}
                      component="img"
                      src={img.thumbnailUrl || img.imageUrl}
                      alt={img.altText || `Thumbnail ${idx + 1}`}
                      onClick={() => setModalImageIndex(idx)}
                      sx={{
                        width: 64,
                        height: 64,
                        objectFit: "cover",
                        borderRadius: 1,
                        cursor: "pointer",
                        border:
                          idx === modalImageIndex
                            ? "2px solid #000"
                            : "1px solid #ddd",
                        opacity: idx === modalImageIndex ? 1 : 0.85,
                        flexShrink: 0,
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          )}

          <Typography variant="body1" sx={{ mb: 1.5 }}>
            {fullDesc}
          </Typography>

          {price > 0 && (
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              ${price.toFixed(2)}
            </Typography>
          )}

          {outOfStock && disabledReason && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {disabledReason}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          {!outOfStock && (
            <Button
              variant="contained"
              onClick={() => {
                onAdd?.();
                handleDialogClose();
              }}
            >
              {hasOptions ? "Customize" : "Add to cart"}
            </Button>
          )}
          <Button onClick={handleDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}